import { useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';

/** The shortest query the search sends (the API refuses shorter ones). */
export const STREAM_SEARCH_MIN_CHARS = 2;

/** How long typing must pause before the search is sent, in ms. */
export const STREAM_SEARCH_DEBOUNCE_MS = 300;

const EMPTY = { performers: { items: [], total: 0 }, songs: { items: [], total: 0 } };

/**
 * The stream page search (`GET /public/bands/{id}/stream/search`, ENG-702):
 * performers plus the band's crate, for the results panel (mockup 18,
 * ENG-701). It searches once typing pauses and the query is long enough;
 * only the answer to the latest query is kept.
 */
export const useStreamSearch = (bandId, term) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [state, setState] = useState({ query: '', results: EMPTY, loading: false, error: false });
  const seqRef = useRef(0);
  const query = (term ?? '').trim();
  const active = [...query].length >= STREAM_SEARCH_MIN_CHARS;

  useEffect(() => {
    const seq = ++seqRef.current;
    if (!bandId || !active) {
      setState({ query: '', results: EMPTY, loading: false, error: false });
      return undefined;
    }
    setState((prev) => ({ ...prev, loading: true, error: false }));
    const timer = setTimeout(() => {
      fetchWrapperRef.current
        .get(`${config.apiBaseUrl}/public/bands/${bandId}/stream/search?q=${encodeURIComponent(query)}`)
        .then((answer) => {
          if (seq !== seqRef.current) return;
          setState({
            query,
            results: {
              performers: { items: answer?.performers?.items ?? [], total: answer?.performers?.total ?? 0 },
              songs: { items: answer?.songs?.items ?? [], total: answer?.songs?.total ?? 0 },
            },
            loading: false,
            error: false,
          });
        })
        .catch(() => {
          if (seq !== seqRef.current) return;
          setState((prev) => ({ ...prev, loading: false, error: true }));
        });
    }, STREAM_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [bandId, query, active]);

  return { ...state, active };
};
